import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';
import type { ITitleNote } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/component/EditNotesModal';

const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

interface IAutoSaveData {
  notes: ITitleNote[];
  baselineNotes: ITitleNote[];
  datatime: string;
}

interface IParams {
  isEditing: boolean;
  monthKey: string;
  notes: ITitleNote[];
  setNotes: (notes: ITitleNote[]) => void;
}

const EMPTY_DATA: IAutoSaveData = {
  notes: [],
  baselineNotes: [],
  datatime: '',
};

export const useStudiosBatchNotesAutoSave = ({
  isEditing,
  monthKey,
  notes,
  setNotes,
}: IParams) => {
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [baselineNotes, setBaselineNotes] = useState<ITitleNote[]>([]);
  const initialParamsRef = useRef<IAutoSaveData>(deepClone(EMPTY_DATA));

  const autoSavePageKey = useMemo(
    () => (monthKey ? `${EDIT_PAGE_KEY_MAP.studio_studios_batch_notes}_${monthKey}` : ''),
    [monthKey],
  );

  const autoSaveMethods = useAutoSaveParams({
    pageKey: autoSavePageKey,
  });

  const {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey,
  } = autoSaveMethods;

  const captureBaseline = useCallback((noteList: ITitleNote[]) => {
    const clonedNotes = deepClone(noteList || []);
    setBaselineNotes(clonedNotes);
    initialParamsRef.current = {
      notes: clonedNotes,
      baselineNotes: clonedNotes,
      datatime: monthKey,
    };
  }, [monthKey]);

  const clearBaseline = useCallback(() => {
    setBaselineNotes([]);
    initialParamsRef.current = deepClone(EMPTY_DATA);
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing || !monthKey) {
      return false;
    }
    return JSON.stringify(notes || []) !== JSON.stringify(baselineNotes || []);
  }, [isEditing, monthKey, notes, baselineNotes]);

  const currentSaveParams = useMemo(() => {
    if (!isEditing || !monthKey) {
      return deepClone(EMPTY_DATA);
    }
    return {
      notes: notes || [],
      baselineNotes: baselineNotes || [],
      datatime: monthKey,
    };
  }, [isEditing, monthKey, notes, baselineNotes]);

  useAutoSaveParams({
    pageKey: autoSavePageKey,
    initialParams: initialParamsRef.current,
    saveParamsCur: currentSaveParams,
    isBegin: isEditing && !!monthKey,
    isChangeValue: true,
  });

  useEffect(() => {
    if (!isEditing) {
      initialParamsRef.current = deepClone(EMPTY_DATA);
    }
  }, [isEditing]);

  const handleEditWithAutoSave = useCallback((onNoRestore: () => void) => {
    if (autoSavePageKey && showRestorePrompt(autoSavePageKey)) {
      setAutoSaveVisible(true);
      return;
    }
    onNoRestore();
  }, [showRestorePrompt, autoSavePageKey]);

  const handleAutoSaveConfirm = useCallback((): IAutoSaveData | null => {
    const savedData = (getSaveParamsValue(autoSavePageKey) || {}) as IAutoSaveData;
    setAutoSaveVisible(false);

    // Ignore stale autosave from a different month context.
    if (savedData.datatime && savedData.datatime !== monthKey) {
      deleteSaveParamsByPageKey(autoSavePageKey);
      return null;
    }

    if (Array.isArray(savedData.notes)) {
      setNotes(savedData.notes);
    }

    const restoredBaseline = savedData.baselineNotes || savedData.notes || [];
    setBaselineNotes(restoredBaseline);
    initialParamsRef.current = {
      notes: savedData.notes || [],
      baselineNotes: restoredBaseline,
      datatime: monthKey,
    };

    deleteSaveParamsByPageKey(autoSavePageKey);
    return {
      notes: savedData.notes || [],
      baselineNotes: restoredBaseline,
      datatime: monthKey,
    };
  }, [
    getSaveParamsValue,
    autoSavePageKey,
    monthKey,
    setNotes,
    deleteSaveParamsByPageKey,
  ]);

  const handleAutoSaveCancel = useCallback(() => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [deleteSaveParamsByPageKey, autoSavePageKey]);

  const clearAutoSave = useCallback(() => {
    clearBaseline();
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [clearBaseline, deleteSaveParamsByPageKey, autoSavePageKey]);

  return {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  };
};
